import React, { useState, useCallback, useEffect } from 'react';
import { Search, Clock, Brain, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useDebounce } from '@/hooks/use-debounce';
import { api } from '@/lib/api';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { VStack, HStack } from '@/components/core';

interface Memory {
  id: string;
  content: string;
  context: string;
  created_at: string;
  similarity_score: number;
  emotional_context?: string;
}

interface MemorySearchProps {
  characterId: number;
  onMemorySelect?: (memory: Memory) => void;
  isModal?: boolean;
  onClose?: () => void;
}

export const MemorySearch: React.FC<MemorySearchProps> = ({
  characterId,
  onMemorySelect,
  isModal = false,
  onClose
}) => {
  const [query, setQuery] = useState('');
  const [memories, setMemories] = useState<Memory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const debouncedQuery = useDebounce(query, 300);

  const searchMemories = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setMemories([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post(
        `/api/v1/memory/character/${characterId}/memories/search`,
        { query: searchQuery }
      );
      setMemories(response.data.memories || []);
    } catch (err: unknown) {
      console.error('Memory search error:', err);
      setError('Fehler bei der Suche');
      setMemories([]);
    } finally {
      setIsLoading(false);
    }
  }, [characterId]);

  useEffect(() => {
    void searchMemories(debouncedQuery);
  }, [debouncedQuery, searchMemories]);

  const highlightText = (text: string, highlight: string) => {
    if (!highlight.trim()) return text;
    
    const regex = new RegExp(`(${highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} className="bg-warning/30 px-0.5 rounded">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const handleMemoryClick = (memory: Memory) => {
    if (onMemorySelect) {
      onMemorySelect(memory);
    }
  };

  const content = (
    <div className="w-full max-w-2xl mx-auto">
      {/* Search Input */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground z-10 pointer-events-none" />
        <Input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Durchsuche Erinnerungen..."
          className="pl-10"
          autoFocus
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-info"></div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-token-sm bg-error/10 text-error rounded-token-button">
          {error}
        </div>
      )}

      {/* Results */}
      <VStack gap="md" className="max-h-96 overflow-y-auto">
        {memories.length === 0 && debouncedQuery && !isLoading && (
          <div className="text-center py-8 text-muted-foreground">
            Keine Erinnerungen gefunden für "{debouncedQuery}"
          </div>
        )}

        {memories.map((memory) => (
          <div
            key={memory.id}
            onClick={() => handleMemoryClick(memory)}
            className="p-token-md bg-card rounded-token-button shadow-token-sm hover:shadow-token-dropdown transition-shadow cursor-pointer border border-border"
          >
            {/* Header */}
            <HStack justify="between" align="center" className="mb-2">
              <HStack gap="sm" align="center">
                <Brain className="h-4 w-4 text-info" />
                <span className="text-sm font-medium text-muted-foreground">
                  Ähnlichkeit: {Math.round(memory.similarity_score * 100)}%
                </span>
              </HStack>
              <HStack gap="sm" align="center" className="text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                {format(new Date(memory.created_at), 'dd. MMM yyyy, HH:mm', { locale: de })}
              </HStack>
            </HStack>

            {/* Content */}
            <div className="mb-2">
              <p className="text-foreground line-clamp-3">
                {highlightText(memory.content, query)}
              </p>
            </div>

            {/* Context */}
            {memory.context && (
              <div className="mb-2">
                <p className="text-sm text-muted-foreground line-clamp-2">
                  <span className="font-medium">Kontext:</span>{' '}
                  {highlightText(memory.context, query)}
                </p>
              </div>
            )}

            {/* Emotional Context */}
            {memory.emotional_context && (
              <HStack gap="sm" align="center">
                <span className="text-xs px-2 py-1 bg-brand-secondary/20 dark:bg-brand-secondary/30 text-brand-secondary dark:text-brand-secondary-light rounded-full">
                  {memory.emotional_context}
                </span>
              </HStack>
            )}
          </div>
        ))}
      </VStack>
    </div>
  );

  if (isModal) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-token-md bg-black/50">
        <div className="bg-card rounded-token-card p-token-lg w-full max-w-2xl max-h-[80vh] overflow-hidden">
          {/* Modal Header */}
          <HStack justify="between" align="center" className="mb-6">
            <h2 className="text-xl font-semibold text-foreground">
              Erinnerungen durchsuchen
            </h2>
            {onClose && (
              <Button
                onClick={onClose}
                variant="ghost"
                size="icon"
                className="h-8 w-8 hover:bg-muted rounded-token-button"
              >
                <X className="h-5 w-5" />
              </Button>
            )}
          </HStack>

          {content}
        </div>
      </div>
    );
  }

  return content;
};